import { useState } from "react";
import { AttendeeRole, AttendeeStatus } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import InviteDialog from "@apps/invite-dialog/invite-dialog";
import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { assertNonNull } from "@helpers/helpers";

import { OnboardingMetricKey, ReportOnboarding } from "../helpers/onboarding";
import useStartReportOnboarding from "../helpers/use-start-report-onboarding";
import { ONBOARDING_METRICS } from "./onboarding-metrics";

// An onboarding "next step" button for a report: shows the first not-yet-completed checklist
// step and runs its action on click (open the profile, schedule a 1-on-1, or invite to the
// app). Self-contained so it works anywhere a report is shown.
const OnboardingProgress = ({
  report,
  onboarding,
}: {
  report: BasicUser;
  onboarding: ReportOnboarding;
}) => {
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();
  const label = useLabel();
  const { startReportOnboarding } = useStartReportOnboarding();
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const nextStep = ONBOARDING_METRICS.find((metric) => !onboarding[metric.key]);

  const handleMetricClick = (key: OnboardingMetricKey) => {
    if (key === "toldAboutReport") {
      startReportOnboarding({ id: report.id, name: report.name });
    } else if (key === "hasRecurringOneonone") {
      setShowMeetingDialog(true);
    } else if (key === "invited") {
      setShowInviteDialog(true);
    }
  };

  if (!nextStep) {
    return null;
  }

  return (
    <>
      {/* Matches the integration cards' "Connect" CTA styling exactly. */}
      <button
        type="button"
        onClick={() => handleMetricClick(nextStep.key)}
        className="inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium text-blue-500 hover:bg-blue-100 hover:text-blue-700"
      >
        {nextStep.label}
        <TopicflowIcon type="chevronRight" className="h-3.5 w-3.5" />
      </button>
      {showMeetingDialog && (
        <MeetingDialog
          formOptions={{
            title: `${label("oneonone")}: ${
              currentUser.firstName || currentUser.name
            } <> ${report.firstName || report.name}`,
            isFormalOneonone: true,
            attendees: [
              {
                ...report,
                email: assertNonNull(report.email),
                role: AttendeeRole.Required,
                participantStatus: AttendeeStatus.NotResponded,
              },
              {
                ...currentUser,
                email: assertNonNull(currentUser.email),
                role: AttendeeRole.Required,
                participantStatus: AttendeeStatus.NotResponded,
              },
            ],
            facilitatorId: currentUser.id,
          }}
          onClose={() => setShowMeetingDialog(false)}
        />
      )}
      {showInviteDialog && (
        <InviteDialog
          organization={organization}
          initialEmails={report.email ? [report.email] : []}
          onClose={() => setShowInviteDialog(false)}
        />
      )}
    </>
  );
};

export default OnboardingProgress;
